import { useCFPValues } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { SafeHtml } from '@bcp-channels-sharedcomponents-reactjs/core';
import {
	Box,
	Chip,
	ComposableTypography,
	ComposedElement,
	ToggleButton,
	useBreakpoint,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { Check } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { css } from '@emotion/css';
import { UIHelper } from '@helpers';
import { useTranslation } from '@hooks';
import { CookiePrivacyPreference } from '@store';

import { DrawerAccordionItem } from './drawerAccordionItem';

interface DrawerContentProps {
	remarketingAccepted?: boolean;
	privacyPreferences?: CookiePrivacyPreference[];
	onRemarketingChipClick?: (accepted: boolean) => void;
	onPrivacyPreferenceToggle?: (idFamily?: string) => void;
}

export const DrawerContent = ({
	remarketingAccepted,
	privacyPreferences,
	onRemarketingChipClick,
	onPrivacyPreferenceToggle,
}: DrawerContentProps) => {
	const { t } = useTranslation();
	const breakpoint = useBreakpoint();
	const { getSpacing } = useCFPValues();

	return (
		<Box
			styles={{
				flexDirection: 'column',
				flex: 1,
				backgroundColor: '#F5F6FA',
				padding: UIHelper.rem(24, 32),
				gap: UIHelper.rem(40),
				overflowY: 'auto',
			}}
		>
			<Box
				styles={{
					backgroundColor: '#FFFFFF',
					borderRadius: UIHelper.rem(8),
					padding: UIHelper.rem(24),
					maxWidth: breakpoint === 'desktop' ? UIHelper.rem(532) : 'unset',
				}}
			>
				{/* Not possible to use an <a></a> with CF ComposableTypography */}
				<div
					className={css`
						> div {
							font-size: 16px;
							font-weight: 500;
							color: #80828f;

							> a {
								font-weight: 600;
								color: #d1005d;
							}
						}
					`}
				>
					<SafeHtml allowedAttributes={['href', 'target']} html={t('drawers.cookiesPreferences.message')} />
				</div>
			</Box>

			<Box
				sx={{
					flexDirection: 'column',
					gap: UIHelper.rem(16),
				}}
			>
				<ComposableTypography
					baseProps={{
						appearance: 'subtitle-100-04',
					}}
					text={t('drawers.cookiesPreferences.subtitle')}
					tags={{}}
				/>

				{privacyPreferences?.map((preference, index) => (
					<DrawerAccordionItem
						key={preference.idFamily || '' + index}
						header={{
							label: preference.familyName,
							endContent: preference.isRequired ? (
								<Box sx={{ alignItems: 'center', gap: UIHelper.rem(8) }}>
									<ComposedElement
										size="xSmall"
										icon={{ source: Check.Fill }}
										sx={{
											fill: '#47C06B',
										}}
									/>

									<ComposableTypography
										baseProps={{
											appearance: 'body-100-03',
											styles: {
												// Couldn't find CF token
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore
												color: '#47C06B',
											},
										}}
										text={t('drawers.cookiesPreferences.alwaysActive')}
										tags={{}}
									/>
								</Box>
							) : (
								<ToggleButton
									initialOn={preference.accepted}
									onPress={(e) => {
										e.stopPropagation();
										onPrivacyPreferenceToggle?.(preference.idFamily);
									}}
									size="small"
								/>
							),
						}}
						body={{
							label: preference.familyDescription,
						}}
					/>
				))}

				<DrawerAccordionItem
					header={{
						label: t('drawers.cookiesPreferences.marketing.label'),
						endContent: (
							<Box
								onPress={(e) => e.stopPropagation()}
								styles={{
									gap: getSpacing({
										default: {
											default: 8,
											mobile: 8,
											laptop: 16,
										},
									}),
									cursor: 'auto',
								}}
							>
								<Chip
									size="small"
									label={t('drawers.cookiesPreferences.marketing.accept')}
									selected={remarketingAccepted}
									onPress={() => onRemarketingChipClick?.(true)}
								/>
								<Chip
									size="small"
									label={t('drawers.cookiesPreferences.marketing.notAccept')}
									selected={!remarketingAccepted}
									onPress={() => onRemarketingChipClick?.(false)}
								/>
							</Box>
						),
					}}
					body={{
						label: t('drawers.cookiesPreferences.marketing.description'),
					}}
				/>
			</Box>
		</Box>
	);
};
